import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'
import {
  TrendingUp,
  TrendingDown,
  Minus,
  MoreVertical,
  Pencil,
  Trash2,
  Sparkles,
  Bookmark,
  Users,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import type { Insight } from '@/lib/mock-data'
import { cn } from '@/lib/utils'
import { TranslatableText } from '@/components/TranslatableText'
import { getDateLocale } from '@/lib/utils/dateLocale'

interface InsightCardProps {
  insight: Insight
  isCurrentUserAdmin?: boolean
  isTracked?: boolean
  trackCount?: number
  isTrackLoading?: boolean
  onInsightClick?: (
    insightId: string,
    ticker: string | undefined,
    sentiment: string,
    content: string
  ) => void
  onImageClick?: (imageUrl: string) => void
  onUserClick?: (userId: string, username: string, displayName?: string) => void
  onEdit?: (insight: Insight) => void
  onDelete?: (insightId: string) => void
  onTrackToggle?: (insightId: string, isTracked: boolean) => void
}

export function InsightCard({
  insight,
  isCurrentUserAdmin = false,
  isTracked = false,
  trackCount = 0,
  isTrackLoading = false,
  onInsightClick,
  onImageClick,
  onUserClick,
  onEdit,
  onDelete,
  onTrackToggle,
}: InsightCardProps) {
  const { t, i18n } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen])

  const sentimentConfig = {
    bullish: {
      icon: TrendingUp,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/20',
      label: t('bullish'),
    },
    bearish: {
      icon: TrendingDown,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-500/20',
      label: t('bearish'),
    },
    neutral: {
      icon: Minus,
      color: 'text-neutral-400',
      bg: 'bg-neutral-500/10',
      border: 'border-neutral-500/20',
      label: t('neutral'),
    },
  }

  const config = sentimentConfig[insight.sentiment]
  const SentimentIcon = config.icon

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    setMenuOpen(false)
    onEdit?.(insight)
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    setMenuOpen(false)
    onDelete?.(insight.id)
  }

  const handleTrackToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    onTrackToggle?.(insight.id, isTracked)
  }

  return (
    <Card
      className="border-white/[0.06] bg-neutral-950/60 backdrop-blur-sm hover:bg-neutral-900/60 hover:border-white/[0.1] transition-all duration-300 cursor-pointer group relative active:scale-[0.98]"
      onClick={() =>
        onInsightClick?.(
          insight.id,
          insight.ticker,
          insight.sentiment,
          insight.content
        )
      }
    >
      {/* Top Right Actions */}
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
        {/* Track Count */}
        {trackCount > 0 && (
          <div className="flex items-center gap-1 text-xs text-neutral-500 mr-1">
            <Users className="w-3 h-3" />
            <span>{trackCount}</span>
          </div>
        )}

        {/* Bookmark Button */}
        <button
          onClick={handleTrackToggle}
          disabled={isTrackLoading}
          className={cn(
            'p-1.5 rounded-lg transition-all',
            isTracked
              ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/10'
              : 'text-neutral-500 hover:text-white hover:bg-white/10',
            isTrackLoading && 'opacity-50 cursor-not-allowed'
          )}
        >
          <Bookmark className={cn('w-4 h-4', isTracked && 'fill-current')} />
        </button>

        {/* Admin Menu */}
        {isCurrentUserAdmin && (
          <div ref={menuRef}>
            <button
              onClick={(e) => {
                e.stopPropagation()
                setMenuOpen(!menuOpen)
              }}
              className="p-1.5 rounded-lg text-neutral-500 hover:text-white hover:bg-white/10 transition-all"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {/* Dropdown Menu */}
            {menuOpen && (
              <div className="absolute right-0 top-8 w-36 bg-neutral-900 border border-white/10 rounded-lg shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                <button
                  onClick={handleEdit}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-neutral-300 hover:bg-white/5 hover:text-white transition-colors"
                >
                  <Pencil className="w-4 h-4" />
                  {t('edit')}
                </button>
                <button
                  onClick={handleDelete}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  {t('delete')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <CardContent className="p-4 space-y-3">
        {/* User Info Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {/* Avatar (clickable) */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                onUserClick?.(
                  insight.userId,
                  insight.username,
                  insight.displayName
                )
              }}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center hover:ring-2 hover:ring-emerald-400/50 transition-all cursor-pointer shadow-lg shadow-emerald-500/10"
            >
              <span className="text-black font-semibold text-sm">
                {(insight.displayName || insight.username)
                  .charAt(0)
                  .toUpperCase()}
              </span>
            </button>

            {/* Username & Timestamp */}
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onUserClick?.(
                      insight.userId,
                      insight.username,
                      insight.displayName
                    )
                  }}
                  className="text-sm font-medium text-white hover:text-emerald-400 transition-colors cursor-pointer"
                >
                  {insight.displayName || insight.username}
                </button>
                {insight.isAdmin && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-medium">
                    {t('admin')}
                  </span>
                )}
              </div>
              <span className="text-xs text-neutral-500 font-mono">
                @{insight.username} •{' '}
                {formatDistanceToNow(
                  typeof insight.createdAt === 'string'
                    ? new Date(insight.createdAt)
                    : insight.createdAt,
                  {
                    addSuffix: true,
                    locale: getDateLocale(i18n.language),
                  }
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <p className="text-sm text-neutral-300 leading-relaxed whitespace-pre-line">
          <TranslatableText text={insight.content} />
        </p>

        {/* Sentiment & Ticker Row */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Sentiment Badge */}
          <div
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border',
              config.bg,
              config.color,
              config.border
            )}
          >
            <SentimentIcon className="w-3.5 h-3.5" />
            {config.label}
          </div>

          {/* Ticker */}
          {insight.ticker && (
            <div className="px-2.5 py-1 rounded-full bg-cyan-500/10 text-cyan-400 text-xs font-semibold border border-cyan-500/20 font-mono">
              ${insight.ticker}
            </div>
          )}

          {/* Tags */}
          {insight.tags.map((tag) => (
            <div
              key={tag}
              className="px-2.5 py-1 rounded-full bg-neutral-800/50 text-neutral-400 text-xs border border-white/[0.06]"
            >
              #{tag}
            </div>
          ))}

          {/* Spacer to push AI button to the right */}
          <div className="flex-1" />

          {/* AI Chat Indicator */}
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-500/10 text-violet-400 text-xs font-medium border border-violet-500/20">
            <Sparkles className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t('askAI')}</span>
          </div>
        </div>

        {/* Image (if present) */}
        {insight.imageUrl && (
          <div className="rounded-lg overflow-hidden border border-white/[0.06] bg-neutral-900/50">
            <img
              src={insight.imageUrl}
              alt={t('tradingChart')}
              className="w-full h-auto object-cover hover:opacity-90 transition-opacity cursor-pointer"
              onClick={(e) => {
                e.stopPropagation()
                onImageClick?.(insight.imageUrl!)
              }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
